import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { adminService } from '@/services/admin';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

export function PersonReportPicker({ kind }: { kind: 'student' | 'teacher' }) {
  const navigate = useNavigate();
  const [personId, setPersonId] = useState('');
  const { data: students } = useQuery({
    queryKey: ['admin-students'],
    queryFn: adminService.listStudents,
    enabled: kind === 'student',
  });
  const { data: teachers } = useQuery({
    queryKey: ['admin-teachers'],
    queryFn: adminService.listTeachers,
    enabled: kind === 'teacher',
  });

  const options = useMemo(() => {
    if (kind === 'student') {
      return (students ?? [])
        .filter((student) => student.status === 'Active')
        .map((student) => ({
          id: student.id,
          label: `${student.firstName} ${student.lastName}${student.className ? ` · ${student.className}` : ''}`,
        }));
    }
    return (teachers ?? [])
      .filter((teacher) => teacher.status === 'Active')
      .map((teacher) => ({
        id: teacher.id,
        label: `${teacher.firstName} ${teacher.lastName}`,
      }));
  }, [kind, students, teachers]);

  const openReport = () => {
    if (!personId) return;
    navigate(kind === 'student' ? `/admin/reports/students/${personId}` : `/admin/reports/teachers/${personId}`);
  };

  return (
    <Card className="p-6">
      <h3 className="mb-1 text-lg font-bold text-slate-800">
        {kind === 'student' ? 'One student report' : 'One teacher report'}
      </h3>
      <p className="mb-4 text-sm text-slate-500">Active people only. Month and year are on the report page.</p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto]">
        <select
          aria-label={kind === 'student' ? 'Student' : 'Teacher'}
          className="w-full rounded-lg border-slate-200 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          value={personId}
          onChange={(event) => setPersonId(event.target.value)}
        >
          <option value="">{kind === 'student' ? 'Choose a student' : 'Choose a teacher'}</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
        <Button onClick={openReport} disabled={!personId} className="bg-indigo-600 font-bold text-white hover:bg-indigo-700">
          Open report
        </Button>
      </div>
    </Card>
  );
}
